/**
 * The action bar shown above the shipments table. Includes a search field and
 * filters to help find shipments. Also includes a create shipment button and
 * actions for bulk-editing shipments.
 */

import PropTypes from "prop-types";
import { TableActionBar } from "@amzn/meridian/table";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Toggle from "@amzn/meridian/toggle";
import HorizontalStickyContainer from "../app/horizontal-sticky-container";
import incomingShipmentPropType from "../../prop-types/incoming-shipment";
import CsvDownloader from 'react-csv-downloader';
import useIncomingShipmentsViewPreferences from "../../hooks/use-incoming-shipments-view-preferences"
import IncomingShipmentsViewPreferences from "./incoming-shipments-view-preferences";
import { getSummaryQuantity } from "../../utilities/parent-child-conversion";
import { getCsvValue } from "../../utilities/csv-helpers";
import { convertParentChild } from "../Constants";
// import IncomingShipmentsUnscheduledViewPreferences from "./incoming-shipments-unscheduled-view-preferences";

function calculateSums(array) {
  let totalExpectedUnits = 0;
  let vendorSum = 0;
  let transshipSum = 0;

  array && array.forEach(item => {
    // Use conversion logic to get appropriate quantity (child or parent)
    const units = getSummaryQuantity(item, 'expectedUnits', 'childExpectedUnits');
    totalExpectedUnits += units;

    // Calculate sums based on loadType
    if (item.loadType === "Vendor") {
      vendorSum += units;
    } else if (item.loadType === "Transship") {
      transshipSum += units;
    }
  });

  return {
    totalSum: totalExpectedUnits,
    vendorSum: vendorSum,
    transshipSum: transshipSum
  };
}

const HorizontalStickyRow = props => (
  <HorizontalStickyContainer Component={Row} {...props} />
)

const IncomingShipmentsScheduledActionBar = props => {
  const {
    incomingShipments,
    table
  } = props
  const [viewPreferences, setViewPreferences] = useIncomingShipmentsViewPreferences()
  const Wrapper = props.stick ? HorizontalStickyRow : Row

  const mapDataForCsv = (table) => {

    let csvColumns = []
    if (table === 'scheduled') {
      csvColumns = ["loadType", "source", "loadId", "tempZone", "scheduledArrivalsParsed",
        "expectedArrivalTimeParsed", "arrivalTimeParsed", "expectedUnits", "expectedAsrsUnits", "expectedAsrsUnitsPercent", "expectedPallets", "shipmentId",
        "scac"
      ]
    }
    else if (table === 'pending') {
      csvColumns = ["loadType", "source", "loadId", "tempZone", "evdd",
        "lvdd", "expectedUnits", "expectedAsrsUnits", "expectedAsrsUnitsPercent", "expectedPallets", "shipmentId",
        "scac"
      ]
    }

    return incomingShipments && incomingShipments.map(data => {
      const selectedData = {};

      csvColumns.forEach(key => {

        if (key === 'evdd') {
          selectedData[key] = data.handoffWindowStartDateUTC
            ? data.handoffWindowStartDateUTC
            : data.departureTime
        }
        else if (key === 'lvdd') {
          selectedData[key] = data.handoffWindowEndDateUTC
            ? data.handoffWindowEndDateUTC
            : data.departureTime
        }
        else {
          selectedData[key] = getCsvValue(data, key);
        }
      });
      return selectedData;
    });
  };

  return (
    <Wrapper widths="fill">
      <TableActionBar alignmentHorizontal="justify">
        <Row spacing="200">
          <Row>
            <Heading level={6}>
              {table === 'scheduled'
                ? `Total Scheduled/Manifested Units${convertParentChild === 1 ? ' Child' : ''}:`
                : `Total Pending/Missing Appt Units${convertParentChild === 1 ? ' Child' : ''}:`
              }
            </Heading>
            <Text>{calculateSums(incomingShipments).totalSum.toLocaleString()}</Text>
            <Heading level={6}>
              {table === 'scheduled' ? '[ Vendor:' : '[ Vendor:'}
            </Heading>
            <Text>{calculateSums(incomingShipments).vendorSum.toLocaleString()}</Text>
            <Heading level={6}>
              {table === 'scheduled' ? 'Transship:' : 'Transship:'}
            </Heading>
            <Text>{calculateSums(incomingShipments).transshipSum.toLocaleString()} <strong>]</strong></Text>

          </Row>
        </Row>
        <Row spacing="400">
          {table === 'scheduled' ? (
            <IncomingShipmentsViewPreferences isScheduled={true} />
          ) : (
            <IncomingShipmentsViewPreferences isScheduled={false} />

          )}
          <Toggle
            checked={viewPreferences.get(table === 'scheduled' ? "expandAll" : "expandAllUnscheduled")}
            onChange={(checked) => {
              const newPrefs = new Map(viewPreferences);
              newPrefs.set(table === 'scheduled' ? "expandAll" : "expandAllUnscheduled", checked);
              setViewPreferences(newPrefs);
            }}
            size="large"
          >
            Aggregate
          </Toggle>
          <CsvDownloader
            datas={mapDataForCsv(table)}
            filename="incoming_shipments"
            extension=".csv"
          >
            <Button>CSV</Button>
          </CsvDownloader>
        </Row>
      </TableActionBar>
    </Wrapper>
  );
}

IncomingShipmentsScheduledActionBar.propTypes = {
  stick: PropTypes.bool.isRequired,
  incomingShipments: PropTypes.arrayOf(incomingShipmentPropType),
  totalIncomingUnits: PropTypes.number,
  totalVendorUnits: PropTypes.number,
  totalTranshipUnits: PropTypes.number,
  table: PropTypes.string,
}

export default IncomingShipmentsScheduledActionBar
